import type { BuiltPlan, PlanStop } from '@ambit/shared';

/**
 * What to call each stop. A stop's label depends on where it falls relative to
 * the hero, not just what slot it fills: the same restaurant is "Eat" after a
 * hike and "Dinner first" on the way to an 8pm concert.
 */
function labelFor(slot: string, isBeforeHero: boolean): string {
  if (slot === 'hero') return 'The reason';
  if (slot === 'food') return isBeforeHero ? 'Dinner first' : 'Eat';
  if (slot === 'addon') return isBeforeHero ? 'Start with' : 'Then';
  return 'Also';
}

interface Props {
  plan: BuiltPlan;
  busySlot: 'food' | 'addon' | null;
  onReroll: (slot: 'food' | 'addon', currentId: string) => void;
  onSave: () => void;
  saving: boolean;
}

/**
 * The signature element: a vertical strip-map. Stops connected by a dashed
 * line, numbers in mono so they line up down the page.
 */
export function RouteCard({ plan, busySlot, onReroll, onSave, saving }: Props) {
  // Constrained even though the page is wider: this is a vertical strip-map and
  // a column of prose. Letting it run to a 4xl measure pushes the reroll link
  // away from the stop it belongs to and stretches blurbs past a comfortable
  // line length. The card grid wants the width; this does not.
  const heroIndex = plan.stops.findIndex((s) => s.slot === 'hero');

  return (
    <section aria-labelledby="route-heading" className="mt-10 max-w-2xl">
      <h2 id="route-heading" className="display text-3xl font-semibold uppercase tracking-tight text-bone-100 sm:text-4xl">
        The day
      </h2>
      <p className="display mt-1 text-xl text-blaze-300">{plan.title}</p>

      <ol className="mt-6 space-y-0">
        {plan.stops.map((stop, index) => (
          <Stop
            key={`${stop.outing.id}-${stop.position}`}
            stop={stop}
            isLast={index === plan.stops.length - 1}
            isBeforeHero={index < heroIndex}
            busy={busySlot === stop.slot}
            onReroll={
              stop.slot === 'food' || stop.slot === 'addon'
                ? () => onReroll(stop.slot as 'food' | 'addon', stop.outing.id)
                : undefined
            }
          />
        ))}
      </ol>

      {plan.notes.length > 0 && (
        <ul className="mt-4 space-y-1">
          {plan.notes.map((note) => (
            <li key={note} className="text-sm text-blaze-300">
              {note}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-spruce-800 pt-4">
        <Total label="Cost" value={`$${plan.totals.totalCost}`} warn={plan.totals.overBudget} />
        <Total label="Hours" value={`${plan.totals.totalHours}`} />
        <Total label="Drive" value={`${plan.totals.maxDrive}m`} />
        {plan.totals.overBudget && (
          <span className="numeric text-xs uppercase text-blaze-400">
            over budget by ${plan.totals.totalCost - plan.totals.budget}
          </span>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="rounded-md bg-blaze-500 px-5 py-2.5 text-sm font-semibold text-spruce-950 transition-colors hover:bg-blaze-400 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save to shortlist'}
        </button>
        <button
          type="button"
          onClick={() => void navigator.clipboard.writeText(asPlainText(plan))}
          className="rounded-md bg-spruce-800 px-5 py-2.5 text-sm text-bone-300 transition-colors hover:bg-spruce-700 hover:text-bone-100"
        >
          Copy for the group chat
        </button>
      </div>
    </section>
  );
}

function Stop({
  stop,
  isLast,
  isBeforeHero,
  busy,
  onReroll,
}: {
  stop: PlanStop;
  isLast: boolean;
  isBeforeHero: boolean;
  busy: boolean;
  onReroll?: () => void;
}) {
  return (
    <li className="relative flex gap-4 pb-6">
      {/* The trail. A real line, not decoration — it is what makes this a route. */}
      {!isLast && (
        <span
          aria-hidden="true"
          className="absolute left-[7px] top-5 h-full border-l-2 border-dashed border-spruce-600"
        />
      )}
      <span
        aria-hidden="true"
        className={`relative z-10 mt-1.5 h-4 w-4 shrink-0 rounded-full ${
          stop.slot === 'hero' ? 'bg-blaze-500 ring-4 ring-blaze-500/20' : 'bg-spruce-600'
        }`}
      />

      <div className={`min-w-0 flex-1 ${busy ? 'opacity-40' : ''}`}>
        <div className="flex items-baseline justify-between gap-3">
          <span className="display text-xs font-semibold uppercase tracking-widest text-blaze-400">
            {labelFor(stop.slot, isBeforeHero)}
          </span>
          {onReroll && (
            <button
              type="button"
              onClick={onReroll}
              disabled={busy}
              className="shrink-0 text-xs text-bone-500 underline decoration-dotted underline-offset-4 transition-colors hover:text-blaze-300 disabled:opacity-50"
            >
              {busy ? 'rolling…' : 'something else'}
            </button>
          )}
        </div>

        <h3 className="display mt-0.5 text-2xl font-semibold leading-tight text-bone-100">
          {stop.outing.name}
        </h3>
        <p className="text-sm text-bone-500">{stop.outing.locality}</p>
        <p className="mt-1.5 text-sm leading-relaxed text-bone-300">{stop.outing.blurb}</p>

        {stop.pairNote && (
          <p className="mt-1.5 text-sm italic text-blaze-300">{stop.pairNote}</p>
        )}
        {stop.outing.bookingNote && (
          <p className="mt-1.5 text-xs text-blaze-300">Note: {stop.outing.bookingNote}</p>
        )}

        <dl className="numeric mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-bone-500">
          <Datum label="cost" value={stop.outing.costUsd === 0 ? 'free' : `$${stop.outing.costUsd}`} />
          <Datum label="hrs" value={String(stop.outing.hours)} />
          <Datum label="drive" value={`${stop.outing.driveMinutes}m`} />
          {stop.outing.effort && <Datum label="effort" value={stop.outing.effort} />}
        </dl>
        {/*
          The caveat that makes the headline number wrong — "per vehicle", "free
          with an NPS pass". It sits under the figures it qualifies, because
          above them it reads as a claim about the stop rather than the price.
        */}
        {stop.outing.costNote && (
          <p className="mt-1.5 text-xs text-bone-500">{stop.outing.costNote}</p>
        )}
      </div>
    </li>
  );
}

function Datum({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-1.5">
      <dt className="text-bone-500">{label}</dt>
      <dd className="text-bone-300">{value}</dd>
    </div>
  );
}

function Total({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div>
      <div className="display text-[10px] uppercase tracking-widest text-bone-500">{label}</div>
      <div className={`numeric text-3xl ${warn ? 'text-blaze-400' : 'text-bone-100'}`}>{value}</div>
    </div>
  );
}

/** Plain text, for pasting into a family text thread. */
function asPlainText(plan: BuiltPlan): string {
  const heroIndex = plan.stops.findIndex((s) => s.slot === 'hero');
  const lines = plan.stops.map(
    (s, i) =>
      `${labelFor(s.slot, i < heroIndex)}: ${s.outing.name} (${s.outing.locality}) — $${s.outing.costUsd}, ${s.outing.hours}h`,
  );
  return [
    plan.title,
    ...lines,
    `Total: $${plan.totals.totalCost}, about ${plan.totals.totalHours}h including ${plan.totals.maxDrive} min each way.`,
  ].join('\n');
}
